import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import Part from '../../../components/HomePart'
import icon1 from '../../../assets/img/high-icon-1.png'
import icon2 from '../../../assets/img/high-icon-2.png'
import icon3 from '../../../assets/img/high-icon-3.png'
import icon4 from '../../../assets/img/high-icon-4.png'
import Glass from '../../../components/Glass'
import './style.scss'
import System from '../../../store/system'

type Tag = 1 | 2 | 3 | 4
interface BoardProps {
  title: string
  content: string
  tag: Tag
  active: boolean
  onActive: (element: HTMLDivElement, tag: Tag) => void
  icon: string
}

const Board: React.FC<BoardProps> = ({
  icon,
  title,
  content,
  tag,
  active,
  onActive,
}) => {
  const { isMobile } = System.useContainer()
  const domRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = (): void => {
    if (domRef.current) {
      onActive(domRef.current, tag)
    }
  }

  return (
    <div
      ref={domRef}
      className={classnames('board', { active })}
      onMouseEnter={handleMouseEnter}
    >
      {isMobile && (
        <div className="board-icon">
          <img src={icon} alt="" />
        </div>
      )}
      <div className="title">{title}</div>
      <div className="content">{content}</div>
    </div>
  )
}

interface GlassBgInfo {
  height: number
  width: number
  left: number
  top: number
}

const HighLight: React.FC = () => {
  const boardRef = useRef<HTMLDivElement>(null)
  const [currentTag, setCurrentTag] = useState<Tag>(1)
  const { t } = useTranslation('trans', { keyPrefix: 'highLight' })
  const [glassBgInfo, setGlassBgInfo] = useState<GlassBgInfo>({
    height: 0,
    width: 0,
    left: 0,
    top: 0,
  })

  const handleActiveRect = useCallback(
    (element: HTMLDivElement, tag: Tag) => {
      setCurrentTag(tag)
      if (boardRef.current) {
        const targetRect = element.getBoundingClientRect()
        const boardRect = boardRef.current.getBoundingClientRect()
        const relativeX = targetRect.x - boardRect.x
        const relativeY = targetRect.y - boardRect.y
        setGlassBgInfo({
          height: targetRect.height,
          width: targetRect.width,
          left: relativeX,
          top: relativeY,
        })
      }
    },
    [setCurrentTag, setGlassBgInfo]
  )

  useEffect(() => {
    if (boardRef.current) {
      handleActiveRect(boardRef.current.childNodes[0] as HTMLDivElement, 1)
    }
  }, [])

  return (
    <Part id="highLight" title={t('title')}>
      <div className="boards" ref={boardRef}>
        <Board
          tag={1}
          icon={icon1}
          title={t('boards.1.title')}
          content={t('boards.1.content')}
          active={currentTag === 1}
          onActive={handleActiveRect}
        />
        <Board
          tag={2}
          icon={icon2}
          title={t('boards.2.title')}
          content={t('boards.2.content')}
          active={currentTag === 2}
          onActive={handleActiveRect}
        />
        <Board
          tag={3}
          icon={icon3}
          title={t('boards.3.title')}
          content={t('boards.3.content')}
          active={currentTag === 3}
          onActive={handleActiveRect}
        />
        <Board
          tag={4}
          icon={icon4}
          title={t('boards.4.title')}
          content={t('boards.4.content')}
          active={currentTag === 4}
          onActive={handleActiveRect}
        />
        <Glass className="bg" style={{ ...glassBgInfo }}></Glass>
      </div>
      <div className="icons">
        <div className={classnames('icon', { active: currentTag === 1 })}>
          <img src={icon1} alt="" />
        </div>
        <div className={classnames('icon', { active: currentTag === 2 })}>
          <img src={icon2} alt="" />
        </div>
        <div className={classnames('icon', { active: currentTag === 3 })}>
          <img src={icon3} alt="" />
        </div>
        <div className={classnames('icon', { active: currentTag === 4 })}>
          <img src={icon4} alt="" />
        </div>
      </div>
    </Part>
  )
}

export default HighLight
